import { observable, computed, runInAction } from 'mobx';
import Store from '~/glm/models/store';
import validators from '~/glm/utils/validators';
import { formatPhone } from '~/glm/utils/strings';
import { getStateAbbreviationByString } from '~/glm/utils/us-states';
import { getCountryCodeByString } from '~/glm/utils/countries';

const fullNameValidation = guest =>
  guest.isNotEmpty ? validators.presence.func(guest.firstName + guest.lastName + guest.names) : true;

class GuestCandidateModel extends Store {
  @observable names = '';
  @observable firstName = '';
  @observable lastName = '';
  @observable phone = '';
  @observable email = '';
  @observable emailError = false;
  @observable phoneError = false;
  @observable fullAddress = '';
  @observable street1 = '';
  @observable street2 = '';
  @observable city = '';
  @observable zip = '';
  @observable state = '';
  @observable country = '';
  @observable role = '';
  @observable plusOne = null;
  @observable partyMembers = [];
  @observable eventIds = [];

  static validations = {
    firstName: fullNameValidation,
    lastName: fullNameValidation,
    names: fullNameValidation,
    email: guest => (guest.email ? validators.email.func(guest.email) : true),
    phone: guest => (guest.phone ? validators.phone.func(formatPhone(guest.phone)) : true),
  };

  static ROLES = [
    'Bride',
    'Groom',
    'Partner',
    'Maid of Honor',
    'Matron of Honor',
    'Man of Honor',
    'Bridesmaid',
    'Bridesman',
    'Best Man',
    'Groomsman',
    'Groomswoman',
    'Usher',
    'Flower Girl',
    'Ring Bearer',
    'Attendant',
  ];

  static TABLE_COLUMN_MAPPING = [
    {
      apiName: 'names',
      humanName: 'Guest Names (Required*)',
    },
    {
      apiName: 'phone',
      humanName: 'Phone Number',
    },
    {
      apiName: 'email',
      humanName: 'Email Address',
    },
    {
      apiName: 'street1',
      humanName: 'Street Address',
    },
    {
      apiName: 'street2',
      humanName: 'Apt/Suite',
    },
    {
      apiName: 'city',
      humanName: 'City',
    },
    {
      apiName: 'state',
      humanName: 'State',
    },
    {
      apiName: 'zip',
      humanName: 'Zip',
    },
  ];

  constructor(props) {
    super();
    runInAction(() => Object.assign(this, props));
  }

  @computed
  get combineAddressComponents() {
    const { street1, street2, city, zip, state, country } = this;

    return [street1, street2, city, zip, state, country].filter(Boolean).join(', ');
  }

  @computed
  get address() {
    if (this.combineAddressComponents.length === 0) {
      return undefined;
    }

    const street1 = this.street1 ? this.street1 : undefined;
    const street2 = this.street2 ? this.street2 : undefined;
    const city = this.city ? this.city : undefined;
    const zip = this.zip ? this.zip.trim() : undefined;
    const state = this.state ? getStateAbbreviationByString(this.state.trim()) : undefined;
    const country = this.country ? getCountryCodeByString(this.country.trim()) : undefined;

    return {
      street1,
      street2,
      city,
      zip,
      state,
      country,
    };
  }

  @computed
  get isValid() {
    return (
      this.isNotEmpty &&
      Object.values(this.constructor.validations).reduce((validity, rule) => validity && rule(this), true)
    );
  }

  @computed
  get validPartyMembers() {
    return this.partyMembers.filter(partyMember => partyMember.isValid);
  }

  validateField(fieldName) {
    const validator = this.constructor.validations[fieldName];
    if (validator) return validator(this);
    return true;
  }

  @computed
  get isNotEmpty() {
    const fields = `
    ${this.names}
    ${this.firstName}
    ${this.lastName}
    ${this.phone}
    ${this.email}
    ${this.street1}
    ${this.street2}
    ${this.city}
    ${this.state}
    ${this.zip}
    `;
    return !!fields.replace(/\s/g, '');
  }

  @computed
  get hasErrors() {
    return this.isNotEmpty && !this.isValid;
  }
}

export default GuestCandidateModel;
